import { Outlet } from 'react-router-dom';
import { config } from '@/lib/env';
import { useInactivityTimeout } from '@/hooks/use-inactivity-timeout';
import { useSessionSignOut } from '@/hooks/use-session-sign-out';
import { useSessionStore } from '@/store/session-store';
import { CookieNotice } from './CookieNotice';
import { SessionExpiryDialog } from './SessionExpiryDialog';
import { MaintenanceBanner } from './MaintenanceBanner';
import { PortalHeader } from './PortalHeader';

/**
 * Chrome shared by every portal screen (US-004): branding, tabs, session controls and the routed
 * content.
 *
 * The shell also renders for an anonymous visitor — the 404 fallback lives inside it — with the tabs
 * and session controls left out and a Sign in link in their place.
 */
export function AppShell(): React.JSX.Element {
  const providerId = useSessionStore((state) => state.providerId);
  const signOut = useSessionSignOut();

  const { remainingMs, staySignedIn } = useInactivityTimeout(
    config.sessionTimeoutMinutes,
    () => {
      signOut('timeout');
    },
    providerId !== null,
    config.sessionWarningMinutes,
  );

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-canvas via-canvas-mid to-canvas-end">
      <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:bg-surface focus:p-3">
        Skip to main content
      </a>

      <MaintenanceBanner />

      <PortalHeader />

      <main id="main" className="mx-auto w-full max-w-shell flex-1 px-6 py-7 sm:px-10">
        <Outlet />
      </main>

      <CookieNotice />

      {remainingMs !== null && (
        <SessionExpiryDialog remainingMs={remainingMs} onStaySignedIn={staySignedIn} />
      )}
    </div>
  );
}
